import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import type { ViewFilter, FilterOperator } from '@mayksel/types';

export interface FieldDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'boolean';
  options?: Array<{ value: string; label: string }>;
}

interface Props {
  fields: FieldDef[];
  filters: ViewFilter[];
  onChange: (filters: ViewFilter[]) => void;
}

const OPERATORS_BY_TYPE: Record<string, FilterOperator[]> = {
  text: ['equals', 'not_equals', 'contains', 'not_contains', 'is_empty', 'is_not_empty'],
  number: ['equals', 'not_equals', 'gt', 'lt', 'gte', 'lte', 'is_empty', 'is_not_empty'],
  date: ['equals', 'not_equals', 'gt', 'lt', 'gte', 'lte', 'is_empty', 'is_not_empty'],
  select: ['equals', 'not_equals', 'is_empty', 'is_not_empty'],
  boolean: ['equals', 'not_equals'],
};

const NO_VALUE_OPERATORS: FilterOperator[] = ['is_empty', 'is_not_empty'];

export default function FilterPanel({ fields, filters, onChange }: Props) {
  const { t } = useTranslation('views');

  function addFilter() {
    const firstField = fields[0];
    if (!firstField) return;
    onChange([
      ...filters,
      { field: firstField.key, operator: 'equals', value: '' },
    ]);
  }

  function updateFilter(index: number, patch: Partial<ViewFilter>) {
    const updated = filters.map((f, i) => (i === index ? { ...f, ...patch } : f));
    onChange(updated);
  }

  function removeFilter(index: number) {
    onChange(filters.filter((_, i) => i !== index));
  }

  function clearAll() {
    onChange([]);
  }

  return (
    <div style={{
      background: 'var(--color-bg)', border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)',
      display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
      minWidth: 480,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
          {t('filters.title')}
        </span>
        {filters.length > 0 && (
          <button
            onClick={clearAll}
            style={{ background: 'none', border: 'none', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 0 }}
          >
            {t('filters.clear_all')}
          </button>
        )}
      </div>

      {/* Filter rows */}
      {filters.length === 0 ? (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-2)' }}>
          {t('filters.no_filters')}
        </p>
      ) : (
        filters.map((filter, index) => {
          const fieldDef = fields.find((f) => f.key === filter.field) ?? fields[0];
          if (!fieldDef) return null;
          const availableOps = OPERATORS_BY_TYPE[fieldDef.type] ?? OPERATORS_BY_TYPE['text']!;
          const noValue = NO_VALUE_OPERATORS.includes(filter.operator);

          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              {/* Field */}
              <select
                value={filter.field}
                onChange={(e) => updateFilter(index, { field: e.target.value, operator: 'equals', value: '' })}
                style={selectStyle}
              >
                {fields.map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
              </select>

              {/* Operator */}
              <select
                value={filter.operator}
                onChange={(e) => updateFilter(index, { operator: e.target.value as FilterOperator })}
                style={selectStyle}
              >
                {availableOps.map((op) => (
                  <option key={op} value={op}>{t(`operators.${op}`)}</option>
                ))}
              </select>

              {/* Value */}
              {!noValue && (
                fieldDef.type === 'select' && fieldDef.options ? (
                  <select
                    value={String(filter.value ?? '')}
                    onChange={(e) => updateFilter(index, { value: e.target.value })}
                    style={{ ...selectStyle, flex: 1 }}
                  >
                    <option value="">—</option>
                    {fieldDef.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : fieldDef.type === 'boolean' ? (
                  <select
                    value={String(filter.value ?? 'true')}
                    onChange={(e) => updateFilter(index, { value: e.target.value === 'true' })}
                    style={{ ...selectStyle, flex: 1 }}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input
                    type={fieldDef.type === 'number' ? 'number' : fieldDef.type === 'date' ? 'date' : 'text'}
                    value={String(filter.value ?? '')}
                    onChange={(e) => updateFilter(index, { value: e.target.value })}
                    style={{ ...selectStyle, flex: 1 }}
                    placeholder={t('filters.value')}
                  />
                )
              )}
              {noValue && <span style={{ flex: 1 }} />}

              {/* Remove */}
              <button
                onClick={() => removeFilter(index)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', flexShrink: 0 }}
              >
                <X size={14} />
              </button>
            </div>
          );
        })
      )}

      {/* Add filter */}
      <button
        onClick={addFilter}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 'var(--text-xs)', color: 'var(--color-brand)', padding: 0,
          alignSelf: 'flex-start',
        }}
      >
        <Plus size={12} /> {t('filters.add_filter')}
      </button>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '3px var(--space-2)',
  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-xs)', color: 'var(--color-text)', background: 'var(--color-bg)',
  cursor: 'pointer', minWidth: 0,
};
